import * as React from "react";
import { Bar, Scatter } from "react-chartjs-2";
import { Button } from "react-materialize";
import { BACKGROUND_COLORS, FUN_THEME_COLORS } from "./colors";

const allStores: Record<string, Store> = require("../allocations/input-data/stores.json");
const allOrders: Record<string, Order> = require("../allocations/input-data/orders.json");
const windowWidth = () => (typeof window !== "undefined" ? window.innerWidth : 1024);

interface Store {
  zip: number;
  lat: number;
  lon: number;
  max_cap: number;
  cost_per_weight: number;
}

interface Order {
  order_no: string;
  order_date: number;
  zipCode: string;
  lat: number;
  lon: number;
  item_no: string;
  item_weight: number;
}

interface Shipment {
  order_no: string;
  items_per_store: Record<string, unknown>;
}

export interface StrategyData {
  solutions_per_day: Record<number, Record<string, Shipment>>;
  capacity_log_per_day: Record<number, number[][]>;
  total_cost_per_day: Record<number, number>;
  p_failure_per_day: Record<number, number>;
  calculation_time_per_day: Record<number, number>;
}

interface Props {
  strategyData: StrategyData;
  strategy: string;
  day: number;
}

export default class SparklingChart extends React.Component<Props> {
  chartInstance: any;
  warehouseChartInstance: any;
  shipmentIndex = 0;
  intervalId: ReturnType<typeof setInterval> | null = null;
  shipmentCounterRef = React.createRef<HTMLSpanElement>();
  solutions: Shipment[] = [];

  startIterating() {
    this.intervalId = setInterval(() => {
      if (!this.solutions.length || this.chartInstance === undefined) return;

      const shipment = this.solutions[this.shipmentIndex];
      if (shipment == null) {
        this.shipmentIndex++;
        return;
      }
      const list: { x: number; y: number }[] = [];
      Object.keys(shipment.items_per_store).forEach((store_key) => {
        const warehouse = allStores[store_key];
        list.push({ x: warehouse.lat, y: warehouse.lon });
        list.push({ x: allOrders[shipment.order_no].lat, y: allOrders[shipment.order_no].lon });
      });
      this.chartInstance.data.datasets[2].data = list;
      this.chartInstance.update("none");
      this.warehouseChartInstance.data.datasets[0].data =
        this.props.strategyData.capacity_log_per_day[this.props.day][this.shipmentIndex];
      this.warehouseChartInstance.update("none");
      if (this.shipmentCounterRef.current) {
        this.shipmentCounterRef.current.innerHTML = String(this.shipmentIndex);
      }
      this.shipmentIndex++;

      if (this.shipmentIndex > this.solutions.length) clearInterval(this.intervalId!);
    }, 100);
  }

  componentDidMount() {
    this.startIterating();
  }

  componentWillUnmount() {
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = null;
  }

  render() {
    this.shipmentIndex = 0;

    const dataAvailable = this.props.day in this.props.strategyData.solutions_per_day;
    this.solutions = dataAvailable ? Object.values(this.props.strategyData.solutions_per_day[this.props.day]) : [];

    if (dataAvailable) {
      const firstShipment = this.solutions[0];
      const firstWarehouse = allStores[Object.keys(firstShipment.items_per_store)[0]];
      const maxCapacity = Math.max(...this.props.strategyData.capacity_log_per_day[this.props.day][0]);

      return (
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", top: 0, right: 0, color: "grey" }}>
            Shipment <span ref={this.shipmentCounterRef}>0</span> / {this.solutions.length}
          </span>
          <Button
            node="button"
            flat={true}
            style={{ position: "absolute", top: 22, right: 0 }}
            onClick={() => {
              if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
              } else {
                this.startIterating();
              }
            }}
          >
            Pause/Resume
          </Button>
          Showing order allocations on day {this.props.day} with strategy <i>{this.props.strategy}</i>.
          <Scatter
            data={{
              datasets: [
                {
                  label: "Stores",
                  data: Object.values(allStores).map((store) => ({ x: store.lat, y: store.lon })),
                  backgroundColor: FUN_THEME_COLORS,
                  pointRadius: 12,
                },
                {
                  label: "Orders",
                  data: this.solutions
                    .filter((s) => s !== null)
                    .map((solution) => ({ x: allOrders[solution.order_no].lat, y: allOrders[solution.order_no].lon })),
                  backgroundColor: "#0058a3",
                },
                {
                  label: "Shipment",
                  data: [
                    { x: firstWarehouse.lat, y: firstWarehouse.lon },
                    { x: allOrders[firstShipment.order_no].lat, y: allOrders[firstShipment.order_no].lon },
                  ],
                  showLine: true,
                  borderColor: "#bbb",
                },
              ],
            }}
            options={{ aspectRatio: windowWidth() > 600 ? 2.5 : 1.4 }}
            ref={(reference) => (this.chartInstance = reference)}
            style={{ marginTop: "0.6cm" }}
          />
          <Bar
            data={{
              labels: Object.keys(allStores),
              datasets: [
                {
                  label: "Warehouse Capacity",
                  data: this.props.strategyData.capacity_log_per_day[this.props.day][this.shipmentIndex],
                  backgroundColor: BACKGROUND_COLORS,
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              indexAxis: "y",
              scales: {
                x: { display: true, max: maxCapacity },
                y: { display: true, title: { display: true, text: "Warehouse Capacity / kg" } },
              },
              aspectRatio: windowWidth() > 600 ? 5.6 : 2.4,
            }}
            ref={(reference) => (this.warehouseChartInstance = reference)}
          />
        </div>
      );
    }
    return <div>No shipment allocations available for day {this.props.day}.</div>;
  }
}
